import { readTravel } from './travel';
import type { RankedWarInfo } from './war';

/**
 * The website's war room (Faction → War; website roadmap S04 — TornStats' war page, FF Scouter's War Room, Torn Intel's
 * live ranked war tracker): the enemy roster's filters and sorting, where each member is, the war-end calculator, and how
 * often the viewer's own key follows the enemy roster. Pure, browser-safe; the page is apps/web/src/app/pages/faction-war.ts.
 *
 * Nothing here asks anyone: the roster comes from the Discord bot's watch (`/api/faction/live`, no cost to the viewer) or
 * from Torn's public member list read with the viewer's own key (`/faction/{id}?selections=basic`), and only the page
 * decides when (FOLLOW_CHOICES, only while it is open and visible, within the site's share of the viewer's calls).
 */

// ---------------------------------------------------------------- one enemy member

export type Activity = 'online' | 'idle' | 'offline';

/** One member of a roster as the war room shows it (times in ms). */
export interface RoomMember {
  id: number;
  name: string;
  level: number;
  /** Torn's state: Okay, Hospital, Jail, Traveling, Abroad, Federal, Fallen. */
  state: string;
  description: string;
  /** When their hospital or jail stay ends (ms). */
  until: number | null;
  /** Their last action (ms). */
  lastAction: number | null;
  activity: Activity;
}

/** Torn's hospital wording abroad ("In a Japanese hospital for 2 hrs") → the country. */
const HOSPITAL_ABROAD: Record<string, string> = {
  mexican: 'Mexico',
  caymanian: 'Cayman Islands',
  canadian: 'Canada',
  hawaiian: 'Hawaii',
  british: 'United Kingdom',
  argentinian: 'Argentina',
  argentine: 'Argentina',
  swiss: 'Switzerland',
  japanese: 'Japan',
  chinese: 'China',
  emirati: 'UAE',
  'south african': 'South Africa',
};

/** Where a member is: in Torn, flying (either way), or abroad — and which country when not in Torn. */
export interface WarPlace {
  where: 'torn' | 'flying' | 'abroad';
  country: string | null;
}

export function placeOf(m: Pick<RoomMember, 'state' | 'description'>): WarPlace {
  const t = readTravel(m.description ?? '');
  if (t) return { where: t.leg === 'there' ? 'abroad' : 'flying', country: t.destination };
  const hosp = /^In an? (.+?) hospital/i.exec(m.description ?? '');
  if (hosp && HOSPITAL_ABROAD[hosp[1]!.toLowerCase()]) return { where: 'abroad', country: HOSPITAL_ABROAD[hosp[1]!.toLowerCase()]! };
  if (/^(traveling|travelling)$/i.test(m.state)) return { where: 'flying', country: null };
  if (/^abroad$/i.test(m.state)) return { where: 'abroad', country: null };
  return { where: 'torn', country: null };
}

/** Fallen (gone from the game) or in federal jail: they won't be hit in this war. */
export const isFallen = (m: Pick<RoomMember, 'state'>): boolean => /^(fallen|federal)$/i.test(m.state);

/** Seconds until they can be hit (0 = now). Hospital or jail: the time left; flying or abroad, fallen: never soon. */
export function untilOkay(m: Pick<RoomMember, 'state' | 'description' | 'until'>, nowMs: number): number {
  if (isFallen(m)) return Number.POSITIVE_INFINITY;
  if (/^(hospital|jail)$/i.test(m.state)) return m.until && m.until > nowMs ? (m.until - nowMs) / 1000 : 0;
  if (placeOf(m).where !== 'torn') return 1e9;
  return 0;
}

// ---------------------------------------------------------------- filters and sorting

export interface RoomFilter {
  /** Only members who can be hit now (Okay, in Torn). */
  okayOnly: boolean;
  /** Hide the fallen and those in federal jail. */
  hideFallen: boolean;
  /** 'any', 'torn' (in Torn), 'away' (flying or abroad), or a country's name. */
  place: string;
  /** Fair fight band (members without one are hidden only when a bound is set). */
  ffMin: number | null;
  ffMax: number | null;
  /** Which activity to show; empty = all. */
  activity: Activity[];
}

export const DEFAULT_ROOM_FILTER: RoomFilter = { okayOnly: false, hideFallen: true, place: 'any', ffMin: null, ffMax: null, activity: [] };

export type RoomSort = 'okay' | 'active' | 'ff' | 'level';
export const ROOM_SORTS: Array<{ id: RoomSort; label: string }> = [
  { id: 'okay', label: 'Okay soonest' },
  { id: 'active', label: 'Last active' },
  { id: 'ff', label: 'Fair fight' },
  { id: 'level', label: 'Level' },
];

const ACTIVITIES: Activity[] = ['online', 'idle', 'offline'];
const ffBound = (x: unknown): number | null => {
  const n = typeof x === 'number' ? x : typeof x === 'string' && x.trim() ? Number(x) : NaN;
  return Number.isFinite(n) && n >= 1 && n <= 3 ? Math.round(n * 100) / 100 : null;
};

/** A filter from storage or a form (anything else → the default's value). */
export function cleanRoomFilter(raw: unknown): RoomFilter {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const place = typeof r.place === 'string' && r.place.length <= 40 ? r.place : 'any';
  let ffMin = ffBound(r.ffMin);
  let ffMax = ffBound(r.ffMax);
  if (ffMin !== null && ffMax !== null && ffMin > ffMax) [ffMin, ffMax] = [ffMax, ffMin];
  return {
    okayOnly: r.okayOnly === true,
    hideFallen: r.hideFallen !== false,
    place,
    ffMin,
    ffMax,
    activity: Array.isArray(r.activity) ? ACTIVITIES.filter((a) => (r.activity as unknown[]).includes(a)) : [],
  };
}

export const cleanRoomSort = (x: unknown): RoomSort => (ROOM_SORTS.some((s) => s.id === x) ? (x as RoomSort) : 'okay');

/** The roster as the filters and the sort say. `ffOf`: the viewer's fair fight against a member, if known. */
export function shapeRoom<T extends RoomMember>(rows: readonly T[], f: RoomFilter, sort: RoomSort, ffOf: (id: number) => number | null, nowMs: number): T[] {
  const list = rows.filter((m) => {
    if (f.hideFallen && isFallen(m)) return false;
    if (f.okayOnly && untilOkay(m, nowMs) > 0) return false;
    if (f.activity.length && !f.activity.includes(m.activity)) return false;
    if (f.place !== 'any') {
      const p = placeOf(m);
      if (f.place === 'torn' ? p.where !== 'torn' : f.place === 'away' ? p.where === 'torn' : p.country !== f.place) return false;
    }
    if (f.ffMin !== null || f.ffMax !== null) {
      const ff = ffOf(m.id);
      if (ff === null || (f.ffMin !== null && ff < f.ffMin - 1e-9) || (f.ffMax !== null && ff > f.ffMax + 1e-9)) return false;
    }
    return true;
  });
  const by: Record<RoomSort, (a: T, b: T) => number> = {
    okay: (a, b) => untilOkay(a, nowMs) - untilOkay(b, nowMs) || (b.lastAction ?? 0) - (a.lastAction ?? 0),
    active: (a, b) => (b.lastAction ?? 0) - (a.lastAction ?? 0),
    ff: (a, b) => (ffOf(b.id) ?? -1) - (ffOf(a.id) ?? -1) || b.level - a.level,
    level: (a, b) => b.level - a.level,
  };
  return list.sort((a, b) => by[sort](a, b) || a.name.localeCompare(b.name));
}

/** The countries the roster's members are in or flying to (for the place filter), in a steady order. */
export function countriesOf(rows: readonly RoomMember[]): string[] {
  return [...new Set(rows.map((m) => placeOf(m).country).filter((c): c is string => !!c))].sort();
}

// ---------------------------------------------------------------- the war-end calculator

/**
 * Torn's rule: the first faction to lead by the war's target wins. After 24 hours the target drops by 1% of the original
 * target every hour (the first drop at the 24-hour mark), so it reaches nothing at 123 hours — the longest a war runs.
 */
export const WAR_DECAY_FROM_H = 24;
export const WAR_DECAY_PER_H = 0.01;
export const WAR_MAX_H = WAR_DECAY_FROM_H + Math.round(1 / WAR_DECAY_PER_H) - 1;

/** The target after `k` drops (whole points, rounded up; no float dust). */
const targetAfter = (target: number, k: number): number => Math.max(0, Math.ceil((target * (100 - k)) / 100 - 1e-9));

/** How many 1% drops have happened by `at` (unix s). */
function dropsAt(start: number, at: number): number {
  const hours = (at - start) / 3600;
  return hours < WAR_DECAY_FROM_H ? 0 : Math.min(100, Math.floor(hours) - WAR_DECAY_FROM_H + 1);
}

/** The lead needed to win at `at` (unix s); null without a target. */
export function warTargetAt(w: Pick<RankedWarInfo, 'start' | 'target'>, at: number): number | null {
  if (!w.target || w.target <= 0) return null;
  return targetAfter(w.target, dropsAt(w.start, at));
}

/**
 * When the war ends if the lead stays `lead` (absolute; unix s): now when it's already enough, else the first hour mark
 * where the target is down to it. A level war (lead 0) ends only when someone leads — null; so does a war without a target.
 */
export function warEndFor(w: Pick<RankedWarInfo, 'start' | 'target'>, lead: number, now: number): number | null {
  const target = warTargetAt(w, now);
  if (target === null) return null;
  lead = Math.abs(lead);
  if (lead <= 0) return null;
  const start = Math.max(now, w.start);
  if (lead >= (warTargetAt(w, start) ?? Infinity)) return start;
  // The target after k drops is ceil(T × (1 − k%)): the fewest drops that bring it to the lead.
  for (let k = dropsAt(w.start, start) + 1; k <= 100; k++) {
    if (targetAfter(w.target!, k) <= lead) return w.start + (WAR_DECAY_FROM_H + k - 1) * 3600;
  }
  return null;
}

/** The next hour marks and the lead each needs (for the calculator's list), from `now`. */
export function warTargetSteps(w: Pick<RankedWarInfo, 'start' | 'target'>, now: number, count = 6): Array<{ at: number; target: number }> {
  if (!w.target) return [];
  const out: Array<{ at: number; target: number }> = [];
  const first = Math.max(dropsAt(w.start, Math.max(now, w.start)) + 1, 1);
  for (let k = first; k <= 100 && out.length < count; k++) {
    const at = w.start + (WAR_DECAY_FROM_H + k - 1) * 3600;
    out.push({ at, target: targetAfter(w.target, k) });
  }
  return out;
}

// ---------------------------------------------------------------- following the enemy roster with the viewer's key

/** 0 = the Discord bot's watch (no calls of yours); else seconds between reads with your key. */
export const FOLLOW_CHOICES = [0, 10, 15, 20, 30, 60] as const;
export type FollowEvery = (typeof FOLLOW_CHOICES)[number];
export const cleanFollow = (x: unknown): FollowEvery => (FOLLOW_CHOICES.includes(Number(x) as FollowEvery) ? (Number(x) as FollowEvery) : 0);
/** Calls a minute a choice costs. */
export const followPerMinute = (every: FollowEvery): number => (every ? Math.ceil(60 / every) : 0);
/** Calls left this minute that a read keeps free for the viewer's own clicks elsewhere on the site. */
export const FOLLOW_KEEP_FREE = 3;

/**
 * Whether to read the enemy roster now: following, the page in view, the last read `every` seconds ago (or a minute
 * after Torn said "too many requests"), and enough of the minute's share left.
 */
export function followDue(o: { every: FollowEvery; visible: boolean; lastAsk: number; backoffUntil: number; callsLeft: number; asking: boolean }, nowMs: number): boolean {
  if (!o.every || !o.visible || o.asking || nowMs < o.backoffUntil) return false;
  if (o.callsLeft <= FOLLOW_KEEP_FREE) return false;
  return nowMs - o.lastAsk >= o.every * 1000 - 250;
}

// ---------------------------------------------------------------- hit claims (S35: the calls board, /api/coord)

/**
 * Hit claims — the "Call" on the faction's board (/api/coord, apps/api/src/coordination.ts), the same ones the extension's
 * war board shows: one member holds a target for the claim's life, others queue behind them and take it over in turn.
 * The faction's leader or co-leader sets the life (30 s – 15 min; 10 min until they do — what calls always lasted) and
 * may wipe every claim.
 */
export const CLAIM_LIFE_MIN_S = 30;
export const CLAIM_LIFE_MAX_S = 15 * 60;
export const CLAIM_LIFE_DEFAULT_S = 10 * 60;
/** The lives a leader picks from (seconds). */
export const CLAIM_LIFE_CHOICES = [30, 60, 90, 120, 180, 300, 600, 900] as const;
/** Claims a member may have open at once: held, or waiting in a target's queue. */
export const MAX_OPEN_CLAIMS = 3;
/** Members waiting behind a target's holder, at most. */
export const MAX_CLAIM_QUEUE = 5;

/** A claim life in seconds (whole, 30 s – 15 min), or null when it isn't one. */
export function cleanClaimLife(x: unknown): number | null {
  const n = typeof x === 'number' ? x : typeof x === 'string' && x.trim() ? Number(x) : NaN;
  return Number.isInteger(n) && n >= CLAIM_LIFE_MIN_S && n <= CLAIM_LIFE_MAX_S ? n : null;
}

/** "30 s", "1 min 30 s", "10 min". */
export function claimLifeLabel(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `${m} min${r ? ` ${r} s` : ''}` : `${r} s`;
}

/** One claim as the board sends it (the calls of kind 'call'). */
export interface ClaimCall {
  targetId: number;
  kind: 'call' | 'med';
  by: { id: number; name: string };
  until: number;
  ally?: number;
  queue?: Array<{ id: number; name: string; at?: number }>;
}

/** One target's claim as the viewer sees it: who holds it, the line behind them, and the viewer's place. */
export interface ClaimView {
  holder: { id: number; name: string; until: number; ally: boolean } | null;
  queue: Array<{ id: number; name: string }>;
  mine: boolean;
  /** The viewer's place in the queue (1 = next), 0 when not waiting. */
  place: number;
}

export function claimFor(calls: readonly ClaimCall[], targetId: number, meId: number | null): ClaimView {
  const own = calls.find((c) => c.kind === 'call' && c.targetId === targetId && !c.ally);
  const c = own ?? calls.find((x) => x.kind === 'call' && x.targetId === targetId);
  const queue = (c?.queue ?? []).map((q) => ({ id: q.id, name: q.name }));
  const at = meId === null ? -1 : queue.findIndex((q) => q.id === meId);
  return {
    holder: c ? { id: c.by.id, name: c.by.name, until: c.until, ally: !!c.ally } : null,
    queue,
    mine: !!c && meId !== null && c.by.id === meId && !c.ally,
    place: at + 1,
  };
}

/** How many claims the viewer has open (held or queued) on their own faction's board. */
export function openClaims(calls: readonly ClaimCall[], meId: number | null): number {
  if (meId === null) return 0;
  return calls.filter((c) => c.kind === 'call' && !c.ally && (c.by.id === meId || (c.queue ?? []).some((q) => q.id === meId))).length;
}
